import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';

const WaliKelasMonitoringPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monitoringData, setMonitoringData] = useState({
    class_name: '',
    total_students: 0,
    avg_jam_kompen: 0,
    avg_jam_kersos: 0,
    avg_apresiasi: 0,
    avg_rata_nilai: 0
  });
  const [waliKelasName, setWaliKelasName] = useState('');
  const [reportCounts, setReportCounts] = useState({
    laporanKts: 0,
    ijinInterupsi: 0,
    ijinTidakMasuk: 0,
    laporanLembur: 0,
    laporanKersos: 0,
    laporanPsm: 0,
    laporanKonseling: 0,
    sp: 0
  });

  // Fetch monitoring data from API
  useEffect(() => {
    const fetchMonitoringData = async () => {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        
        // Check if we're in admin mode by looking at the URL
        const urlParams = new URLSearchParams(window.location.search);
        const classParam = urlParams.get('class');
        const userRole = localStorage.getItem('userRole');
        
        let apiUrl;
        if (userRole === 'admin_user' && classParam) {
          // Admin mode with specific class
          apiUrl = `${apiBase}/api/admin/wali-kelas-monitoring?class=${encodeURIComponent(classParam)}`;
        } else {
          // Teacher mode (default)
          apiUrl = `${apiBase}/api/teacher/wali-kelas-monitoring`;
        }
        
        const res = await authFetch(apiUrl, {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to fetch monitoring data: ${res.status} ${errorText}`);
        }
        
        const data = await res.json();
        
        if (data.success && data.data) {
          setMonitoringData(data.data);
          setWaliKelasName(data.data.wali_kelas_name || '');
          
          // Fetch report counts for this wali kelas
          await fetchReportCounts(data.data.class_name);
        } else {
          throw new Error(data.message || 'Invalid response format');
        }
        
        setLoading(false);
      } catch (e) {
        console.error('Error fetching monitoring data:', e);
        setError(e.message);
        setLoading(false);
      }
    };
    
    fetchMonitoringData();
  }, []);

  // Fetch report counts for the wali kelas
  const fetchReportCounts = async (className) => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      const userRole = localStorage.getItem('userRole');
      
      // Determine endpoints based on user role
      let endpoints;
      if (userRole === 'admin_user') {
        // Admin endpoints - fetch all data and filter by class on frontend
        endpoints = [
          { key: 'laporanKts', url: `${apiBase}/api/admin/kts-reports` },
          { key: 'ijinInterupsi', url: `${apiBase}/api/admin/interuption-permits` },
          { key: 'ijinTidakMasuk', url: `${apiBase}/api/admin/absent-permits` },
          { key: 'laporanLembur', url: `${apiBase}/api/admin/overtime-reports` },
          { key: 'laporanKersos', url: `${apiBase}/api/admin/kersos-reports` },
          { key: 'laporanPsm', url: `${apiBase}/api/admin/psm-reports` },
          { key: 'laporanKonseling', url: `${apiBase}/api/admin/konseling` },
          { key: 'sp', url: `${apiBase}/api/admin/surat-peringatan` }
        ];
      } else {
        // Teacher endpoints - filtered by class
        endpoints = [
          { key: 'laporanKts', url: `${apiBase}/api/teacher/kts-reports?class=${encodeURIComponent(className)}` },
          { key: 'ijinInterupsi', url: `${apiBase}/api/teacher/interuption-permits?class=${encodeURIComponent(className)}` },
          { key: 'ijinTidakMasuk', url: `${apiBase}/api/teacher/absent-permits?class=${encodeURIComponent(className)}` },
          { key: 'laporanLembur', url: `${apiBase}/api/teacher/overtime-reports?class=${encodeURIComponent(className)}` },
          { key: 'laporanKersos', url: `${apiBase}/api/teacher/kersos-reports?class=${encodeURIComponent(className)}` },
          { key: 'laporanPsm', url: `${apiBase}/api/teacher/psm-reports?class=${encodeURIComponent(className)}` },
          { key: 'laporanKonseling', url: `${apiBase}/api/teacher/konseling?class=${encodeURIComponent(className)}` },
          { key: 'sp', url: `${apiBase}/api/teacher/warning-letters?class=${encodeURIComponent(className)}` }
        ];
      }
      
      const counts = {};
      
      for (const endpoint of endpoints) {
        try {
          const res = await authFetch(endpoint.url, {
            headers: { 'Accept': 'application/json' }
          });
          
          if (res.ok) {
            const data = await res.json();
            let items = data.data || data || [];
            
            // For admin endpoints, filter by class
            if (userRole === 'admin_user' && Array.isArray(items)) {
              items = items.filter(item => {
                // Handle different class field names and nested student data
                if (item.students && Array.isArray(item.students)) {
                  // Check if any student in the report belongs to the specified class
                  return item.students.some(student => {
                    const studentClass = student.class || student.kelas || student.class_name || student.student_class;
                    return studentClass === className;
                  });
                }
                // Fallback for direct class fields
                const itemClass = item.student?.class || item.class || item.student_class;
                return itemClass === className;
              });
            }
            
            counts[endpoint.key] = Array.isArray(items) ? items.length : 0;
          } else {
            counts[endpoint.key] = 0;
          }
        } catch (e) {
          console.error(`Error fetching ${endpoint.key}:`, e);
          counts[endpoint.key] = 0;
        }
      }
      
      setReportCounts(counts);
    } catch (e) {
      console.error('Error fetching report counts:', e);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>Memuat data monitoring...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
          <RefreshButton onClick={() => window.location.reload()}>
            Muat Ulang
          </RefreshButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <BackButton onClick={() => {
              const userRole = localStorage.getItem('userRole');
              if (userRole === 'admin_user') {
                navigate('/admin/manajemen-guru');
              } else {
                navigate('/teacher');
              }
            }}>
              ← Kembali
            </BackButton>
            <h1>Wali Kelas Monitoring {monitoringData.class_name}</h1>
            {waliKelasName && (
              <WaliKelasInfo>Wali Kelas: <strong>{waliKelasName}</strong></WaliKelasInfo>
            )}
          </div>
        </HeaderContent>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatIcon>👥</StatIcon>
          <StatContent>
            <StatValue>{monitoringData.total_students}</StatValue>
            <StatLabel>Total Siswa di Kelas {monitoringData.class_name}</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon>⏰</StatIcon>
          <StatContent>
            <StatValue>{monitoringData.avg_jam_kompen}</StatValue>
            <StatLabel>Rata-rata Jam Kompen</StatLabel>
            <StatUnit>jam per siswa</StatUnit>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon>🏞️</StatIcon>
          <StatContent>
            <StatValue>{monitoringData.avg_jam_kersos}</StatValue>
            <StatLabel>Rata-rata Jam Kersos</StatLabel>
            <StatUnit>jam per siswa</StatUnit>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon>🎖️</StatIcon>
          <StatContent>
            <StatValue>{monitoringData.avg_apresiasi}</StatValue>
            <StatLabel>Rata-rata Poin Apresiasi</StatLabel>
            <StatUnit>poin per siswa</StatUnit>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon>📊</StatIcon>
          <StatContent>
            <StatValue>{monitoringData.avg_rata_nilai}</StatValue>
            <StatLabel>Rata-rata Nilai</StatLabel>
            <StatUnit>nilai per siswa</StatUnit>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ReportsSection>
        <SectionTitle>Laporan, Ijin dan SP</SectionTitle>
        <ReportsGrid>
          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/kts?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>📋</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.laporanKts}</ReportValue>
              <ReportLabel>Laporan KTS</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/interupsi?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>⏸️</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.ijinInterupsi}</ReportValue>
              <ReportLabel>Ijin Interupsi</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/tidak-masuk?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>🚫</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.ijinTidakMasuk}</ReportValue>
              <ReportLabel>Ijin Tidak Masuk</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/lembur?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>🕐</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.laporanLembur}</ReportValue>
              <ReportLabel>Laporan Lembur</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/kersos?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>🏞️</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.laporanKersos}</ReportValue>
              <ReportLabel>Laporan Kersos</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/psm?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>🔧</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.laporanPsm}</ReportValue>
              <ReportLabel>Laporan PSM</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/konseling?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>💬</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.laporanKonseling}</ReportValue>
              <ReportLabel>Laporan Konseling</ReportLabel>
            </ReportContent>
          </ReportCard>

          <ReportCard onClick={() => {
            const userRole = localStorage.getItem('userRole');
            const basePath = userRole === 'admin_user' ? '/admin' : '/teacher';
            navigate(`${basePath}/wali-kelas-reports/sp?class=${encodeURIComponent(monitoringData.class_name)}`);
          }}>
            <ReportIcon>⚠️</ReportIcon>
            <ReportContent>
              <ReportValue>{reportCounts.sp}</ReportValue>
              <ReportLabel>SP</ReportLabel>
            </ReportContent>
          </ReportCard>
        </ReportsGrid>
      </ReportsSection>

    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const BackButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const WaliKelasInfo = styled.p`
  color: var(--text-dark);
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
  
  strong {
    color: var(--primary, #3b82f6);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingText = styled.div`
  font-size: 1.125rem;
  color: var(--text-light);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 1rem;
`;

const ErrorText = styled.div`
  font-size: 1.125rem;
  color: var(--danger, #c01c28);
  text-align: center;
`;

const RefreshButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const StatCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 1rem;
  }
  
  @media (max-width: 400px) {
    padding: 1rem;
    gap: 0.75rem;
    flex-direction: column;
    text-align: center;
  }
`;

const StatIcon = styled.div`
  font-size: 3rem;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 400px) {
    font-size: 2rem;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-dark);
  line-height: 1;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const StatUnit = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const ReportsSection = styled.div`
  margin-top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const ReportCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: var(--box-shadow);
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 0.75rem;
  }
  
  @media (max-width: 400px) {
    padding: 1rem;
    gap: 0.5rem;
    flex-direction: column;
    text-align: center;
  }
`;

const ReportIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1.5rem;
  }
`;

const ReportContent = styled.div`
  flex: 1;
`;

const ReportValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-dark);
  line-height: 1;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1.125rem;
  }
`;

const ReportLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;



export default WaliKelasMonitoringPage;